import React, {SyntheticEvent, useState} from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import {useTheme} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './custom-quill-styles.css'
import {InputAdornment} from '@mui/material';
import {Loader} from "../../common/Loader";
import {useMutation} from "@apollo/client";
import {CREATE_PACKAGE} from "../../../api/package/createPackage";
import {ErrorMessage} from "../../common/ErrorMessage";
import {UPDATE_PACKAGE} from "../../../api/package/updatePackage";

const initialFormData = {
    code: '',
    description: '',
    id: '',
    price: 0,
    title: '',
}

const modules = {
    toolbar: [
        ['bold', 'italic', 'underline','strike'],
        [{'list': 'ordered'}, {'list': 'bullet'}],
    ],
    clipboard: {
        matchVisual: false
    },
}

const formats = [
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
]

type Package = {
    code: string,
    description: string,
    id: string,
    price: number,
    title: string,
}

type AddEditModalProps = {
    onClose: (shouldRefetch: boolean) => void,
    packageData?: undefined | Package
}

export const AddEditModal = (props: AddEditModalProps) => {
    const {onClose, packageData} = props

    const [formData, setFormData] = useState<Package>(packageData === undefined ? initialFormData : packageData)
    const [isSnackBarOpen, setIsSnackBarOpen] = useState(false)

    const [createPackage, createPackageData] = useMutation(CREATE_PACKAGE)
    const [updatePackage, updatePackageData] = useMutation(UPDATE_PACKAGE)

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    const updateFormData = (newValues: Partial<typeof initialFormData>) => {
        setFormData(oldState => ({
            ...oldState,
            ...newValues,
        }))
    }

    const onPriceChange = (value: string) => {
        const formattedValue = Number(value)

        if (!isNaN(formattedValue) && formattedValue >= 0) {
            updateFormData({price: formattedValue})
        }
    }

    const onSubmit = async (event: SyntheticEvent) => {
        event.preventDefault()

        if (formData.id === '') {
            const {errors} = await createPackage({variables: {input: {
                code: formData.code,
                price: formData.price,
                description: formData.description,
                title: formData.title,
            }}})

            if (errors === undefined || errors.length === 0) {
                onClose(true)
            } else {
                setIsSnackBarOpen(true)
            }
        } else {
            const {errors} = await updatePackage({variables: {input: {
                code: formData.code,
                description: formData.description,
                id: formData.id,
                price: formData.price,
                title: formData.title,
            }}})

            if (errors === undefined || errors.length === 0) {
                onClose(true)
            } else {
                setIsSnackBarOpen(true)
            }
        }
    }

    return (
        <Dialog
            fullScreen={fullScreen}
            open={true}
            onClose={() => onClose(false)}
            fullWidth
            maxWidth="md"
        >
            <form onSubmit={onSubmit}>
                <DialogTitle>
                    {packageData === undefined ? 'Add New Package' : `Edit ${packageData.title}`}
                </DialogTitle>
                <DialogContent>
                    <Stack paddingTop="1rem" gap="1.5rem">
                        <TextField
                            label="Code"
                            variant="outlined"
                            value={formData.code}
                            onChange={(e) => updateFormData({code: e.target.value})}
                        />
                        <TextField
                            label="Title"
                            variant="outlined"
                            value={formData.title}
                            onChange={(e) => updateFormData({title: e.target.value})}
                        />
                        <ReactQuill
                            formats={formats}
                            modules={modules}
                            onChange={(value) => updateFormData({description: value})}
                            theme="snow"
                            value={formData.description}
                            placeholder="Description"
                        />
                        <TextField
                            label="Price"
                            variant="outlined"
                            value={formData.price === 0 ? '' : formData.price}
                            onChange={(e) => onPriceChange(e.target.value)}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">SAR</InputAdornment>,
                            }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => onClose(false)}
                        variant="contained"
                        color="error"
                    >
                        Close
                    </Button>
                    <Button
                        color="success"
                        type="submit"
                        variant="contained"
                        disabled={formData.code === '' || formData.title === ''}
                    >
                        {packageData === undefined ? 'Add' : 'Edit'}
                    </Button>
                </DialogActions>
            </form>

            <Loader isVisible={createPackageData.loading || updatePackageData.loading} />

            <ErrorMessage
                handleClose={() => setIsSnackBarOpen(false)}
                isOpen={isSnackBarOpen}
                message={formData.id === '' ? createPackageData.error?.message : updatePackageData.error?.message}
            />
        </Dialog>
    )
}
